import * as React from "react";

import { useIsDebugMode } from "@editor/components/editor/debug/useIsDebugMode";
import DashboardMenuItem from "@editor/components/shops-ui/DashboardMenuItem";
import { DashboardMenuItems } from "@editor/components/shops-ui/DashboardMenuItems";
import ShopSelector from "@editor/components/shops-ui/ShopSelector";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import {
  useLocalStorage,
  useLocalStorageState,
} from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { storeToken } from "@editor/reducers/utils/store-token";
import { useEditorDispatch, useEditorSelector } from "@editor/store";
import { routes } from "@editor/utils/router";
import { trpc } from "@editor/utils/trpc";
import {
  selectInternalDebugModeOn,
  setDebugPanelVisibility,
} from "@reducers/core-reducer";

import AnnouncementsMenu from "@/features/announcements/AnnouncementsMenu";
import IconButton from "@replo/design-system/components/button/IconButton";
import Separator from "@replo/design-system/components/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  ArrowUpRight,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CircleHelp,
  FileText,
  GraduationCap,
  Slack,
  Store,
  ThumbsUp,
  Upload,
  User,
  UserCheck,
} from "lucide-react";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

const DashboardSidebar = () => {
  const dispatch = useEditorDispatch();
  const location = useLocation();
  const { from } = location.state ?? {};
  const isAllProjectsRoute = location.pathname.startsWith(routes.allProjects);
  const shouldHideItems = isAllProjectsRoute || from === "allProjects";
  const logEvent = useLogAnalytics();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  const [isShopSelectorPopoverOpen, setIsShopSelectorPopoverOpen] =
    React.useState(false);
  const isDebugPanelVisible = useEditorSelector(selectInternalDebugModeOn);
  const isReploDebugMode = useIsDebugMode();
  const localStorage = useLocalStorage();
  const handleDebug = React.useCallback(() => {
    dispatch(setDebugPanelVisibility(!isDebugPanelVisible));
  }, [dispatch, isDebugPanelVisible]);

  const { mutateAsync: logout } = trpc.auth.logout.useMutation({
    onSuccess: async () => {
      storeToken(null);
      window.location.replace("/auth/login");
    },
  });
  const [isSidebarExpanded, setIsSidebarExpanded] = useLocalStorageState(
    "replo.dashboard.sidebarExpanded",
    true,
    { schema: z.boolean() },
  );

  const currentPathname = location.pathname;

  const isItemSelected = (path: string) => {
    return currentPathname.startsWith(path);
  };
  const userItem = {
    title: user?.email ?? "",
    className: "text-default",
    startEnhancer: <User size={16} className="shrink-0" />,
    endEnhancer: <ChevronRight size={16} className="shrink-0" />,
    menuItems: [
      {
        type: "leaf" as const,
        id: "my-account",
        title: "My Account",
        variant: "default" as const,
        size: "sm" as const,
        startEnhancer: <User size={16} />,
        onSelect: () => {
          logEvent("dashboard.nav.click", {
            tab: "your_profile",
            userId: user?.id ?? null,
            workspaceId: workspaceId ?? null,
          });
          navigate(
            generatePath(routes.shops.settings.profile, {
              projectId,
            }),
          );
        },
      },
      {
        // NOTE (Sebas, 2024-12-04): We need this to be a section to add
        // the separator between the two items.
        type: "section" as const,
        items: [
          {
            type: "leaf" as const,
            id: "logout",
            title: "Logout",
            variant: "default" as const,
            size: "sm" as const,
            startEnhancer: <Upload className="rotate-90" size={16} />,
            onSelect: () => {
              logEvent("dashboard.logout", {});
              void logout({});

              // Note (Noah, 2025-02-22): Make sure to clear the last visited
              // workspace when logging out, otherwise when you log in again to
              // a new account you'll see a permissions error by default
              localStorage.removeItem("replo.workspace.lastvisitedWorkspaceId");
            },
          },
        ],
      },
    ],
  };

  const showCollapseButton = isSidebarExpanded || !isShopSelectorPopoverOpen;

  return (
    // NOTE (Max, 2025-04-09): We're just hiding the sidebar on mobile rather adapting it to mobile, as only a small
    // amount of users will ever go to the dashboard on mobile.
    <div
      className={twMerge(
        "border-0.5 border-border shadow-panel hidden flex-col justify-between rounded-[10px] bg-white md:flex relative",
        isSidebarExpanded ? "w-56" : "w-16",
      )}
    >
      <div className="flex flex-col gap-3 p-3">
        <ShopSelector
          isSidebarExpanded={isSidebarExpanded}
          onPopoverOpenChange={setIsShopSelectorPopoverOpen}
        />
        <div
          className={twMerge("relative", isSidebarExpanded ? "pr-2" : "pr-0.5")}
        >
          <Separator />
          {showCollapseButton && (
            <div className="absolute top-[-12px] right-[-24px]">
              <IconButton
                size="sm"
                variant="secondary"
                icon={
                  isSidebarExpanded ? (
                    <ChevronsLeft size={12} />
                  ) : (
                    <ChevronsRight size={12} />
                  )
                }
                onClick={() => {
                  setIsSidebarExpanded(!isSidebarExpanded);
                }}
              />
            </div>
          )}
        </div>
        {!shouldHideItems && (
          <div>
            <DashboardMenuItems isSidebarExpanded={isSidebarExpanded} />
          </div>
        )}
      </div>
      <div
        className={twMerge(
          "flex flex-col gap-3 px-3 pb-4",
          !isSidebarExpanded && "items-center",
        )}
      >
        {isReploDebugMode && (
          <DashboardMenuItem
            item={{
              title: "Replo Debug",
              startEnhancer: (
                <span role="img" aria-label="Debug">
                  🔮
                </span>
              ),
              customWidth: 224,
              onClick: () => {
                handleDebug();
              },
            }}
            isSidebarExpanded={isSidebarExpanded}
          />
        )}
        <AnnouncementsMenu
          source="dashboard"
          isSidebarExpanded={isSidebarExpanded}
        />
        <DashboardMenuItem
          item={HELP_CENTER_ITEM}
          isSidebarExpanded={isSidebarExpanded}
        />
        <DashboardMenuItem
          item={{
            title: "All Shops",
            startEnhancer: <Store size={16} />,
            isSelected: isItemSelected(routes.allProjects),
            href: routes.allProjects,
          }}
          isSidebarExpanded={isSidebarExpanded}
        />
        <Separator className="my-2" />
        <DashboardMenuItem
          item={userItem}
          isSidebarExpanded={isSidebarExpanded}
          nestedMenuAlignOffset={-16}
        />
      </div>
    </div>
  );
};

const RESOURCES_LINKS = [
  {
    title: "Reploversity",
    href: "https://www.replo.app/learn",
    startEnhancer: <GraduationCap size={16} />,
    endEnhancer: <ArrowUpRight size={16} />,
  },
  {
    title: "Help Docs",
    href: "https://support.replo.app/",
    startEnhancer: <FileText size={16} />,
    endEnhancer: <ArrowUpRight size={16} />,
  },
  {
    title: "Find a Replo Expert",
    href: "https://contra.com/replo",
    startEnhancer: <UserCheck size={16} />,
    endEnhancer: <ArrowUpRight size={16} />,
  },
  {
    title: "Join the Community",
    href: "https://join.slack.com/t/replocommunity/shared_invite/zt-2gltcnfjn-dsVdlL4gc36SecPBjw0Zaw",
    startEnhancer: <Slack size={16} />,
    endEnhancer: <ArrowUpRight size={16} />,
  },
  {
    title: "Feedback",
    href: "https://feedback.replo.app/?utm_source=dashboard",
    startEnhancer: <ThumbsUp size={16} />,
    endEnhancer: <ArrowUpRight size={16} />,
  },
];

const HELP_CENTER_ITEM = {
  title: "Help Center",
  startEnhancer: <CircleHelp size={16} />,
  hasSubItems: true,
  customWidth: 224,
  menuItems: RESOURCES_LINKS.map((link) => ({
    type: "leaf" as const,
    id: link.title.toLowerCase().replace(" ", "-"),
    title: link.title,
    variant: "custom" as const,
    size: "base" as const,
    startEnhancer: link.startEnhancer,
    endEnhancer: link.endEnhancer,
    onSelect: () => {
      window.open(link.href, "_blank", "noopener,noreferrer");
    },
  })),
};

export default DashboardSidebar;
